import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Table, Button, Text, Badge, Group, Stack, Title } from '@mantine/core';
import { IconTrendingDown, IconTrendingUp } from '@tabler/icons';
import { SYMBOLS } from '../../../constants/symbols';
import { buildApiUrl } from '../../../config/endpoints';
import { useWaveConfig, getExitFlowType } from '../../../waves/WaveConfigContext';
import ObstructionCloseModal from '../../../waves/obstruction/ObstructionCloseModal';
import './ShortPositionsComponent.css';

// GraphQL query for short positions
import { gql } from '@apollo/client';

const GET_SHORT_POSITIONS = gql`
  query GetShortPositions($ownerId: Int!) {
    shortPositions(ownerId: $ownerId) {
      id
      userId
      walletId
      symbol
      quantity
      entryPrice
      borrowedAt
      status
      borrowRate
      accumulatedFees
      lastFeeCalculation
    }
  }
`;

// Helper function to format numbers
const formatNumber = (value, options = {}) => {
  const {
    prefix = '',
    suffix = '',
    decimalScale = 2,
    thousandSeparator = true,
    fixedDecimals = false  // New option to force exact decimal places
  } = options;

  if (value === null || value === undefined || value === '') return `${prefix}0${suffix}`;

  if (decimalScale === null || decimalScale === undefined) {
    return `${prefix}${value.toString()}${suffix}`;
  }

  const num = Number(value);
  if (!Number.isFinite(num)) {
    return `${prefix}${value.toString()}${suffix}`;
  }

  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: fixedDecimals ? decimalScale : 0,
    maximumFractionDigits: decimalScale,
    useGrouping: thousandSeparator
  });

  return `${prefix}${formatted}${suffix}`;
};export default function ShortPositionsComponent({ ownerId, symbol, onCoverPosition }) {
  const [currentPrices, setCurrentPrices] = useState({});
  // Wave E — Obstruction: when the enrolled participant is in Phase 2/3 of an
  // obstruction wave, the "Cover" tap is gated behind the close-attempt flow
  // instead of covering immediately.
  const { config: waveConfig } = useWaveConfig();
  const [obstructionPosition, setObstructionPosition] = useState(null);
  const parsedOwnerId = Number(ownerId);
  const hasValidOwnerId = Number.isInteger(parsedOwnerId) && parsedOwnerId > 0;
  
  const { data, loading, error } = useQuery(GET_SHORT_POSITIONS, {
    variables: { ownerId: parsedOwnerId },
    skip: !hasValidOwnerId,
    pollInterval: 60000, // Refresh every 1 minute
    onError: (error) => {
      console.error('Short positions query error:', error);
    }
  });

  const positions = useMemo(() => {
    const all = data?.shortPositions || [];
    if (!symbol) return all;
    return all.filter(p => p.symbol.toUpperCase() === symbol.toUpperCase());
  }, [data?.shortPositions, symbol]);

  const enrichedPositions = useMemo(() => {
    return positions.map((position) => {
      const quantity = Number(position.quantity) || 0;
      const entryPrice = Number(position.entryPrice) || 0;
      const borrowRate = Number(position.borrowRate) || 0;
      const accumulatedFees = Number(position.accumulatedFees) || 0;
      const currentPrice = currentPrices[position.symbol] ?? null;
      const grossPnl = currentPrice ? (entryPrice - currentPrice) * quantity : 0;
      const pnlPercent = currentPrice && entryPrice
        ? ((entryPrice - currentPrice) / entryPrice) * 100
        : 0;
      const estimatedDailyFee = currentPrice
        ? (quantity * currentPrice * borrowRate) / 365
        : 0;
      const netPnl = grossPnl - accumulatedFees;

      return {
        ...position,
        metrics: {
          quantity,
          entryPrice,
          borrowRate,
          accumulatedFees,
          currentPrice,
          grossPnl,
          pnlPercent,
          estimatedDailyFee,
          netPnl,
        },
      };
    });
  }, [positions, currentPrices]);

  const totals = useMemo(() => {
    return enrichedPositions.reduce(
      (acc, position) => {
        acc.grossPnL += position.metrics.grossPnl;
        acc.netPnL += position.metrics.netPnl;
        acc.accruedFees += position.metrics.accumulatedFees;
        acc.dailyFees += position.metrics.estimatedDailyFee;
        return acc;
      },
      { grossPnL: 0, netPnL: 0, accruedFees: 0, dailyFees: 0 }
    );
  }, [enrichedPositions]);

  // Fetch current prices for P&L calculation
  useEffect(() => {
    const fetchCurrentPrices = async () => {
      if (positions.length === 0) return;

      const symbols = [...new Set(positions.map(pos => pos.symbol))];
      const prices = {};
      
      for (const symbol of symbols) {
        try {
          // Try multiple ways to find the symbol config
          let symbolConfig = SYMBOLS.find(s => s.code === symbol);
          if (!symbolConfig) {
            symbolConfig = SYMBOLS.find(s => s.code === symbol.toLowerCase());
          }
          if (!symbolConfig) {
            symbolConfig = SYMBOLS.find(s => s.symbol === symbol);
          }
          if (!symbolConfig) {
            symbolConfig = SYMBOLS.find(s => s.symbol === symbol.replace('USD', ''));
          }
          
          if (symbolConfig) {
            const response = await fetch(`${buildApiUrl('/latestprice')}?symbol=${symbolConfig.altcode}`);
            if (response.ok) {
              const priceData = await response.json();
              prices[symbol] = parseFloat(priceData.latest_price);
            }
          } else {
            // Try different formats for the API call
            const possibleFormats = [
              symbol, // Original symbol (e.g., "BTCUSD")
              `${symbol.slice(0, -3)}-${symbol.slice(-3)}`, // Convert "BTCUSD" to "BTC-USD"
              `${symbol.slice(0, -4)}-${symbol.slice(-4)}`, // Convert "ETHUSD" to "ETH-USD" 
            ];
            
            for (const format of possibleFormats) {
              try {
                const directResponse = await fetch(`${buildApiUrl('/latestprice')}?symbol=${format}`);
                if (directResponse.ok) {
                  const directPriceData = await directResponse.json();
                  prices[symbol] = parseFloat(directPriceData.latest_price);
                  break; // Success, stop trying other formats
                }
              } catch (directErr) {
                // Continue to next format
              }
            }
          }
        } catch (err) {
          console.error(`Exception while fetching price for ${symbol}:`, err);
        }
      }
      
      setCurrentPrices(prices);
    };

    fetchCurrentPrices();
    const interval = setInterval(fetchCurrentPrices, 60000); // Update prices every 1 minute
    
    return () => clearInterval(interval);
  }, [positions]);

  const coverNow = (position) => {
    if (onCoverPosition) {
      onCoverPosition(position);
    }
  };

  const handleCoverPosition = (position) => {
    // Gate the close behind the obstruction flow only when it actually applies.
    if (getExitFlowType(waveConfig) !== "normal") {
      setObstructionPosition(position);
      return;
    }
    coverNow(position);
  };

  if (loading) return <Text>Loading short positions...</Text>;
  if (error) return <Text color="red">Error loading short positions: {error.message}</Text>;

  if (positions.length === 0) {
    return (
      <Stack spacing="md" className="short-positions-empty">
        <Title order={4}>Short Positions</Title>
        <Text color="dimmed" size="sm">No short positions found</Text>
      </Stack>
    );
  }

  const rows = enrichedPositions.map((position) => {
    const { metrics } = position;
    const symbolConfig = SYMBOLS.find(s => s.code === position.symbol);
    
    return (
      <tr key={position.id}>
        <td>
          <Group spacing="xs">
            {symbolConfig?.icon}
            <Text weight={500}>{position.symbol}</Text>
          </Group>
        </td>
        <td>
          <Text>
            {formatNumber(metrics.quantity, {
              decimalScale: 9,
              thousandSeparator: true,
              fixedDecimals: true
            })}
          </Text>
        </td>
        <td>
          <Text>
            {formatNumber(metrics.entryPrice, {
              prefix: '$',
              decimalScale: 2,
              thousandSeparator: true
            })}
          </Text>
        </td>
        <td>
          <Text color={metrics.currentPrice > 0 ? undefined : 'dimmed'}>
            {metrics.currentPrice ? formatNumber(metrics.currentPrice, {
              prefix: '$',
              decimalScale: 2,
              thousandSeparator: true
            }) : 'Loading...'}
          </Text>
        </td>
        <td>
          <Stack spacing={2}>
            <Text weight={500}>
              {formatNumber(metrics.borrowRate * 100, {
                suffix: '%',
                decimalScale: 2,
                fixedDecimals: true
              })}
            </Text>
            <Text size="xs" color="dimmed">Annual</Text>
          </Stack>
        </td>
        <td>
          <Text color={metrics.estimatedDailyFee > 0 ? undefined : 'dimmed'}>
            {metrics.estimatedDailyFee > 0 ? formatNumber(metrics.estimatedDailyFee, {
              prefix: '$',
              decimalScale: 2,
              thousandSeparator: true
            }) : '—'}
          </Text>
        </td>
        <td>
          <Text>
            {formatNumber(metrics.accumulatedFees, {
              prefix: '$',
              decimalScale: 2,
              thousandSeparator: true
            })}
          </Text>
        </td>
        <td>
          <Group spacing="xs">
            <Text 
              color={metrics.grossPnl >= 0 ? 'green' : 'red'}
              weight={500}
            >
              {formatNumber(Math.abs(metrics.grossPnl), {
                prefix: metrics.grossPnl >= 0 ? '+$' : '-$',
                decimalScale: 2,
                thousandSeparator: true
              })}
            </Text>
            <Badge 
              color={metrics.grossPnl >= 0 ? 'green' : 'red'} 
              variant="light"
              leftSection={metrics.grossPnl >= 0 ? <IconTrendingUp size={12} /> : <IconTrendingDown size={12} />}
            >
              {metrics.pnlPercent >= 0 ? '+' : ''}{metrics.pnlPercent.toFixed(2)}%
            </Badge>
          </Group>
          <Text size="sm" color="dimmed">
            Net: {formatNumber(Math.abs(metrics.netPnl), {
              prefix: metrics.netPnl >= 0 ? '+$' : '-$',
              decimalScale: 2,
              thousandSeparator: true
            })}
          </Text>
        </td>
        <td>
          <Badge color={position.status === 'OPEN' ? 'blue' : 'gray'}>
            {position.status}
          </Badge>
        </td>
        <td>
          <Button 
            size="xs" 
            color="blue"
            variant="outline"
            onClick={() => handleCoverPosition(position)}
            disabled={position.status !== 'OPEN'}
          >
            Cover
          </Button>
        </td>
      </tr>
    );
  });

  return (
    <Stack spacing="md" className="short-positions-container">
      <Group position="apart" align="flex-start">
        <Title order={4}>Short Positions</Title>
        <Group spacing="xl" align="flex-end" className="short-positions-summary">
          <Stack spacing={2} align="flex-end">
            <Text size="sm" color="dimmed">Net P&amp;L</Text>
            <Text weight={600} color={totals.netPnL >= 0 ? 'green' : 'red'}>
              {formatNumber(Math.abs(totals.netPnL), {
                prefix: totals.netPnL >= 0 ? '+$' : '-$',
                decimalScale: 2,
                thousandSeparator: true
              })}
            </Text>
          </Stack>
          <Stack spacing={2} align="flex-end">
            <Text size="sm" color="dimmed">Fees Paid</Text>
            <Text weight={500}>
              {formatNumber(totals.accruedFees, {
                prefix: '$',
                decimalScale: 2,
                thousandSeparator: true
              })}
            </Text>
          </Stack>
          <Stack spacing={2} align="flex-end">
            <Text size="sm" color="dimmed">Daily Fee Est.</Text>
            <Text weight={500}>
              {formatNumber(totals.dailyFees, {
                prefix: '$',
                decimalScale: 2,
                thousandSeparator: true
              })}
            </Text>
          </Stack>
        </Group>
      </Group>
      
      <Table striped highlightOnHover>
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Quantity</th>
            <th>Entry Price</th>
            <th>Current Price</th>
            <th>Borrow Rate</th>
            <th>Daily Fee</th>
            <th>Accrued Fees</th>
            <th>P&amp;L</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </Table>

      {obstructionPosition && (
        <ObstructionCloseModal
          opened={!!obstructionPosition}
          position={obstructionPosition}
          waveConfig={waveConfig}
          onComplete={() => {
            const pos = obstructionPosition;
            setObstructionPosition(null);
            coverNow(pos);
          }}
          onAbandon={() => setObstructionPosition(null)}
          onFallbackClose={() => {
            const pos = obstructionPosition;
            setObstructionPosition(null);
            coverNow(pos);
          }}
        />
      )}
    </Stack>
  );
}
